import React, { useState, useEffect, useRef } from 'react';
import './SinglePageApp.css';
import serverAPI from '../services/serverAPI';
// Logo will be referenced from public folder

// Input sanitization utilities
const sanitizeInput = (input) => {
  if (typeof input !== 'string') return '';
  const noHtml = input.replace(/<[^>]*>/g, '');
  const noSqlInjection = noHtml.replace(/['";\\]/g, '');
  const noControlChars = noSqlInjection.replace(/[\x00-\x1F\x7F]/g, '');
  return noControlChars.trim();
};

// Validation utilities
const validateName = (name) => {
  if (!name || name.length < 1) return 'Name is required';
  if (name.length > 50) return 'Name must be 50 characters or less';
  if (!/^[a-zA-Z\s\-'\.]+$/.test(name)) return 'Name can only contain letters, spaces, hyphens, apostrophes, and periods';
  return null;
};
const validateDateOfBirth = (dob) => {
  if (!dob) return 'Date of birth is required';
  const date = new Date(dob);
  const now = new Date();
  const minDate = new Date('1900-01-01');
  
  if (isNaN(date.getTime())) return 'Invalid date format';
  if (date > now) return 'Date of birth cannot be in the future';
  if (date < minDate) return 'Date of birth cannot be before 1900';
  return null;
};
const validateEmail = (email) => {
  if (!email || email.length < 1) return 'Email is required';
  if (email.length > 254) return 'Email must be 254 characters or less';
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  if (!emailRegex.test(email)) return 'Please enter a valid email address';
  return null;
};
const validatePhone = (phone) => {
  if (!phone || phone.length < 1) return 'Phone number is required';
  const digitsOnly = phone.replace(/\D/g, '');
  if (digitsOnly.length !== 10) return 'Please enter a complete 10-digit phone number';
  if (!/^\d{10}$/.test(digitsOnly)) return 'Please enter a valid 10-digit phone number';
  return null;
};
const validateAddress = (address) => {
  if (!address || address.length < 1) return 'Address is required if providing address information';
  if (address.length > 100) return 'Address must be 100 characters or less';
  if (!/^[a-zA-Z0-9\s\-'\.#&,]+$/.test(address)) return 'Address contains invalid characters';
  return null;
};
const validateCity = (city) => {
  if (!city || city.length < 1) return 'City is required if providing address information';
  if (city.length > 50) return 'City must be 50 characters or less';
  if (!/^[a-zA-Z\s\-'\.]+$/.test(city)) return 'City can only contain letters, spaces, hyphens, apostrophes, and periods';
  return null;
};
const validateState = (state) => {
  if (!state || state.length < 1) return 'State is required if providing address information';
  if (state.length !== 2) return 'State must be exactly 2 characters';
  if (!/^[A-Z]{2}$/.test(state.toUpperCase())) return 'State must be a valid 2-letter state code';
  return null;
};
const validateZipCode = (zipCode) => {
  if (!zipCode || zipCode.length < 1) return 'ZIP code is required if providing address information';
  if (!/^\d{5}(-\d{4})?$/.test(zipCode)) return 'Please enter a valid ZIP code (5 digits or 5+4 format)';
  return null;
};
const validateOfficeCode = (officeCode) => {
  if (!officeCode || officeCode.length < 1) return 'Office code is required';
  // if (!/^\d{6}$/.test(officeCode)) return 'Office code must be exactly 6 digits';
  return null;
};

// Formatting utilities
const formatPhoneNumber = (value) => {
  const phoneNumber = value.replace(/\D/g, '');
  if (phoneNumber.length === 0) return '';
  if (phoneNumber.length <= 3) {
    return `(${phoneNumber}`;
  } else if (phoneNumber.length <= 6) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  } else {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  }
};

const SinglePageApp = () => {
  // Main state management
  const [currentStep, setCurrentStep] = useState('contact-info');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Contact info form data (now includes dateOfBirth and officeCode)
  const [contactFormData, setContactFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    officeCode: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: ''
  });
  const [contactErrors, setContactErrors] = useState({});
  const [showAddressSection, setShowAddressSection] = useState(false);
  
  // Office codes state
  const [availableOfficeCodes, setAvailableOfficeCodes] = useState([]);
  const [isLoadingOfficeCodes, setIsLoadingOfficeCodes] = useState(false);
  const [officeLogo, setOfficeLogo] = useState(null);
  const [officeCode, setOfficeCode] = useState(null);
  const [showOfficeCodeDropdown, setShowOfficeCodeDropdown] = useState(true);
  
  // Legal documents state
  const [documents, setDocuments] = useState([]);
  const [legalDocumentsAccepted, setLegalDocumentsAccepted] = useState(false);
  const [showLegalDocumentsModal, setShowLegalDocumentsModal] = useState(false);
  const [acknowledgments, setAcknowledgments] = useState({});
  const [expandedDocuments, setExpandedDocuments] = useState({});
  const [readStatus, setReadStatus] = useState({});
  const contentRefs = useRef({});
  
  // Marketing consent state
  const [marketingConsent, setMarketingConsent] = useState(false);
  
  // Enrollment confirmation popup state
  const [showEnrollmentPopup, setShowEnrollmentPopup] = useState(false);
  const [enrollmentData, setEnrollmentData] = useState(null);
  const [isUpdate, setIsUpdate] = useState(false);
  
  // Thank you page state
  const [showSupportModal, setShowSupportModal] = useState(false);
  
  // Offers state
  const [offers, setOffers] = useState([]);
  const [isLoadingOffers, setIsLoadingOffers] = useState(false);
  const [offersError, setOffersError] = useState('');

  const fetchOfficeData = async (forceSetDefault = false) => {
    try {
      // Fetch available office codes
      setIsLoadingOfficeCodes(true);
      console.log('Fetching office codes...');
      const officeCodes = await serverAPI.getOfficeCodes();
      console.log('Office codes received:', officeCodes);
      setAvailableOfficeCodes(officeCodes);
      
      // Set default location if none is selected or if forceSetDefault is true
      // if the location is not in the officeCodes array, set it to null
      // if the url has a location, set it to the location if it is in the officeCodes array else set it to null
      if (window.location.search.includes('location=')) {
        const locationName = decodeURIComponent(window.location.search.split('location=')[1]);
        if (officeCodes.some(location => location.name === locationName)) {
          setContactFormData(prev => ({
            ...prev,
            officeCode: locationName
          }));
          setOfficeCode(locationName);
          // Hide the office code dropdown since we have a valid location from URL
          setShowOfficeCodeDropdown(false);
        } else {
          setContactFormData(prev => ({
            ...prev,
            officeCode: null
          }));
          setOfficeCode(null);
          // Show the office code dropdown since URL location is invalid
          setShowOfficeCodeDropdown(true);
        }
      } else if ((!contactFormData.officeCode || forceSetDefault) && officeCodes.length > 0) {
        console.log('Setting default location:', officeCodes[0].name);
        setContactFormData(prev => ({
          ...prev,
          officeCode: officeCodes[0].name
        }));
        setOfficeCode(officeCodes[0].name);
      } else {
        setContactFormData(prev => ({
          ...prev,
          officeCode: null
        }));
        setOfficeCode(null);
      }
    } catch (error) {
      console.error('Error fetching office data:', error);
    } finally {
      setIsLoadingOfficeCodes(false);
    }
  };
  const fetchOfficeLogo = async () => {
    try {
      const logoURL = await serverAPI.getOfficeLogo(officeCode);
      setOfficeLogo(logoURL);
    } catch (error) {
      console.log('Error fetching office logo:', error);
    }
  };
  const fetchLegalDocuments = async () => {
    if (!contactFormData.officeCode || contactFormData.officeCode === '') {
      setDocuments([]);
      setReadStatus({});
      setAcknowledgments({});
      setExpandedDocuments({});
      return;
    }

    try {
      // Fetch legal documents from the database
      const response = await serverAPI.getLegalDocuments(contactFormData.officeCode);
      
      // If no legal documents found in database, return
      if (response.legalDocuments.length === 0) {
        console.log('No legal documents found in database');
        setDocuments([]);
        setReadStatus({});
        setAcknowledgments({});
        setExpandedDocuments({});
        return;
      }
      setDocuments(response.legalDocuments);
      
      // Initialize document state for modal
      const initialReadStatus = {};
      const initialAcknowledgments = {};
      
      response.legalDocuments.forEach(doc => {
        initialReadStatus[doc.id] = false;
        initialAcknowledgments[doc.id] = false;
      });
      setReadStatus(initialReadStatus);
      setAcknowledgments(initialAcknowledgments);
    } catch (error) {
      console.error('Error fetching legal documents:', error);
    }
  };

  const fetchOffers = async () => {
    if (!contactFormData.officeCode || contactFormData.officeCode === '') {
      setOffers([]);
      return;
    }

    try {
      setIsLoadingOffers(true);
      setOffersError('');
      const response = await serverAPI.getOffers(contactFormData.officeCode);
      setOffers(response.offers || []);
    } catch (error) {
      console.error('Error fetching offers:', error);
      setOffersError('Failed to load offers. Please try again later.');
      setOffers([]);
    } finally {
      setIsLoadingOffers(false);
    }
  };

  // Fetch office codes and logo when component mounts
  useEffect(() => {
    fetchOfficeData();
  }, []);

  // Fetch legal documents and office logo when office code changes
  useEffect(() => {
    if (officeCode) {
      fetchOfficeLogo();
    } else {
      setOfficeLogo(null);
    }
    fetchLegalDocuments();
  }, [officeCode]);

  // Fetch offers when thank-you page loads
  useEffect(() => {
    if (currentStep === 'thank-you' && contactFormData.officeCode) {
      fetchOffers();
    }
  }, [currentStep, contactFormData.officeCode]);

  // Step navigation functions
  const goToStep = (step) => {
    setCurrentStep(step);
    setError('');
  };

  const handleStartOver = async () => {
    // Reset form data
    setContactFormData({ firstName: '', lastName: '', dateOfBirth: '', officeCode: '', email: '', phone: '', address: '', city: '', state: '', zipCode: '' });
    setShowAddressSection(false);
    setIsUpdate(false);
    setError('');

    // Reset legal documents state
    setDocuments([]);
    setLegalDocumentsAccepted(false);
    setShowLegalDocumentsModal(false);
    setAcknowledgments({});
    setExpandedDocuments({});
    setReadStatus({});
    
    // Reset marketing consent
    setMarketingConsent(false);
    
    // Reset enrollment popup state
    setShowEnrollmentPopup(false);
    setEnrollmentData(null);
    
    // Reset office state
    setOfficeLogo(null);
    setOfficeCode(null);
    
    // Fetch office data and set default office code
    await fetchOfficeData(true); // forceSetDefault = true
    
    goToStep('contact-info');
  };


  // Contact info handlers
  const handleContactInputChange = async (e) => {
    const { name, value } = e.target;
    
    let formattedValue = value;

    if (name === 'firstName' || name === 'lastName') {
      formattedValue = sanitizeInput(value);
    } else if (name === 'email') {
      formattedValue = sanitizeInput(value.toLowerCase());
    } else if (name === 'phone') {
      formattedValue = formatPhoneNumber(value);
    } else if (name === 'address' || name === 'city') {
      formattedValue = sanitizeInput(value);
    } else if (name === 'state') {
      formattedValue = sanitizeInput(value.toUpperCase());
    } else if (name === 'zipCode') {
      formattedValue = sanitizeInput(value);
    } else if (name === 'officeCode') {
      formattedValue = value;
    }

    setContactFormData(prev => ({
      ...prev,
      [name]: formattedValue
    }));
    
    if (contactErrors[name]) {
      setContactErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateContactForm = () => {
    const newErrors = {};
    
    const firstNameError = validateName(contactFormData.firstName);
    const lastNameError = validateName(contactFormData.lastName);
    const dobError = validateDateOfBirth(contactFormData.dateOfBirth);
    const officeCodeError = validateOfficeCode(contactFormData.officeCode);
    if (firstNameError) newErrors.firstName = firstNameError;
    if (lastNameError) newErrors.lastName = lastNameError;
    if (dobError) newErrors.dateOfBirth = dobError;
    if (officeCodeError) newErrors.officeCode = officeCodeError;
    
    const emailError = validateEmail(contactFormData.email);
    if (emailError) newErrors.email = emailError;
    
    const phoneError = validatePhone(contactFormData.phone);
    if (phoneError) newErrors.phone = phoneError;
    
    // Check if legal documents need to be accepted
    if (documents.length > 0 && !allAccepted) {
      newErrors.legalDocuments = 'You must read and accept all legal documents to continue';
    }
    
    // Check if marketing consent is provided
    if (!marketingConsent) {
      newErrors.marketingConsent = 'You must consent to receiving text messages and emails to continue';
    }
    
    const hasAddressData = contactFormData.address.trim() || contactFormData.city.trim() || contactFormData.state.trim() || contactFormData.zipCode.trim();
    
    if (hasAddressData) {
      const addressError = validateAddress(contactFormData.address);
      const cityError = validateCity(contactFormData.city);
      const stateError = validateState(contactFormData.state);
      const zipCodeError = validateZipCode(contactFormData.zipCode);
      
      if (addressError) newErrors.address = addressError;
      if (cityError) newErrors.city = cityError;
      if (stateError) newErrors.state = stateError;
      if (zipCodeError) newErrors.zipCode = zipCodeError;
    }

    setContactErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateContactForm()) {
      setError('Please correct the errors above before continuing.');
      return;
    }

      setLoading(true);
    setError('');

    try {
      // Sanitize all form data before sending to server
      const sanitizedFormData = {
        firstName: sanitizeInput(contactFormData.firstName),
        lastName: sanitizeInput(contactFormData.lastName),
        dateOfBirth: sanitizeInput(contactFormData.dateOfBirth),
        officeCode: sanitizeInput(contactFormData.officeCode),
        email: sanitizeInput(contactFormData.email.toLowerCase()),
        phone: sanitizeInput(contactFormData.phone),
        address: sanitizeInput(contactFormData.address),
        city: sanitizeInput(contactFormData.city),
        state: sanitizeInput(contactFormData.state.toUpperCase()),
        zipCode: sanitizeInput(contactFormData.zipCode)
      };
      const sanitizedOfficeCode = sanitizeInput(contactFormData.officeCode);

      console.log('sanitizedFormData:', sanitizedFormData);
      
      // Collect consent metadata
      const consentData = collectConsentData();

      // console.log('Acknowledged legal document IDs for lookup:', acknowledgedDocumentIds);
      // console.log('Consent data for lookup:', consentData);

      const response = await serverAPI.Lookup(sanitizedFormData, sanitizedOfficeCode, consentData);
      
      if (!response.success) {
        setError('Error: Please check the fields and try again.');
        return;
      }

      // Handle enrollment status
      if (response.enrollment.isEnrolledAtCurrent || response.enrollment.isEnrolledElsewhere) {
        setEnrollmentData(response.enrollment);
        setIsUpdate(true);
        setShowEnrollmentPopup(true);
        return;
      }

      // If auto-enrolled or already enrolled, go directly to finish page
      goToStep('thank-you');
      
    } catch (error) {
      console.error('Error during enrollment process:', error);
      if (error.message.includes('Permission denied') || error.message.includes('User denied geolocation')) {
        setError('Location access is required to continue. Please allow location permissions in your browser and try again.');
      } else if (error.message.includes('Failed to fetch') || error.message.includes('Network Error')) {
        setError('Connection failed. Please check your internet connection and try again.');
      } else if (error.message.includes('Patient does not exist')) {
        setError('Error: Please check your name and date of birth.');
      } else {
        setError('Enrollment failed. Please try again or contact support if the problem persists.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Legal documents handlers
  const handleAcknowledgmentChange = (documentId, accepted) => {
    if (!readStatus[documentId]) return;
    setAcknowledgments(prev => ({
      ...prev,
      [documentId]: accepted
    }));
    
    // Clear validation errors when all documents are accepted
    const newAcknowledgments = { ...acknowledgments, [documentId]: accepted };
    const allAccepted = documents.every(doc => newAcknowledgments[doc.id]);
    if (allAccepted && contactErrors.legalDocuments) {
      setContactErrors(prev => ({
        ...prev,
        legalDocuments: null
      }));
    }
  };

  const handleToggleDocument = (documentId) => {
    setExpandedDocuments(prev => {
      const isExpanding = !prev[documentId];
      const next = { ...prev, [documentId]: isExpanding };
      setTimeout(() => {
        const el = contentRefs.current[documentId];
        if (isExpanding && el) {
          const noOverflow = el.scrollHeight <= el.clientHeight + 2;
          if (noOverflow && !readStatus[documentId]) {
            setReadStatus(prevRead => ({ ...prevRead, [documentId]: true }));
          }
        }
      }, 0);
      return next;
    });
  };

  const handleScroll = (documentId) => {
    const el = contentRefs.current[documentId];
    if (!el) return;
    const thresholdPx = 12;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - thresholdPx;
    if (atBottom && !readStatus[documentId]) {
      setReadStatus(prev => ({ ...prev, [documentId]: true }));
    }
  };

  const handleLegalDocumentsModalOpen = () => {
    setShowLegalDocumentsModal(true);
  };

  const handleLegalDocumentsModalClose = () => {
    setShowLegalDocumentsModal(false);
  };


  // Calculate if all documents are accepted
  const allAccepted = documents.every(doc => acknowledgments[doc.id]);

  // Helper function to collect consent metadata
  const collectConsentData = () => {
    const consentData = {
      checkboxStatus: acknowledgments,
      marketingConsent: marketingConsent,
      ipAddress: null, // Will be set by backend from the request
      languageCode: navigator.language || 'en',
      userAgent: navigator.userAgent,
      stateDetected: null, // Will be set by backend from the request
      versionHashes: documents.reduce((hashes, doc) => {
        // Create a simple hash based on document content and timestamp
        hashes[doc.id] = btoa(doc.title + doc.content.substring(0, 100)).substring(0, 16);
        return hashes;
      }, {})
    };
    
    return consentData;
  };

  // Enrollment popup handlers
  const handleEnrollmentContinue = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Sanitize all form data before sending to server
      const sanitizedFormData = {
        firstName: sanitizeInput(contactFormData.firstName),
        lastName: sanitizeInput(contactFormData.lastName),
        dateOfBirth: sanitizeInput(contactFormData.dateOfBirth),
        officeCode: sanitizeInput(contactFormData.officeCode),
        email: sanitizeInput(contactFormData.email.toLowerCase()),
        phone: sanitizeInput(contactFormData.phone),
        address: sanitizeInput(contactFormData.address),
        city: sanitizeInput(contactFormData.city),
        state: sanitizeInput(contactFormData.state.toUpperCase()),
        zipCode: sanitizeInput(contactFormData.zipCode)
      };

      // Collect consent metadata
      const consentData = collectConsentData();

      // console.log('Consent data:', consentData);

      // Use the Enroll endpoint to complete the enrollment with legal document agreements
      const response = await serverAPI.Enroll(sanitizedFormData, sanitizedFormData.officeCode, consentData);
      
      if (!response.success) {
        setError('Error: Please check the fields and try again.');
        return;
      }

      setShowEnrollmentPopup(false);
      
      // Go directly to finish page (legal documents will be handled via checkbox)
      goToStep('thank-you');
      
    } catch (error) {
      console.error('Error during enrollment confirmation:', error);
      if (error.message.includes('Permission denied') || error.message.includes('User denied geolocation')) {
        setError('Location access is required to continue. Please allow location permissions in your browser and try again.');
      } else if (error.message.includes('Failed to fetch') || error.message.includes('Network Error')) {
        setError('Connection failed. Please check your internet connection and try again.');
      } else if (error.message.includes('Patient does not exist')) {
        setError('Error: Please check your name and date of birth.');
      } else {
        setError('Enrollment failed. Please try again or contact support if the problem persists.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEnrollmentCancel = () => {
    setShowEnrollmentPopup(false);
    goToStep('contact-info');
  };


  // Thank you page handlers
  const handleDownloadCard = async () => {
    try {
      setLoading(true);
      await serverAPI.downloadCard(officeCode);
    } catch (error) {
      console.error('Failed to download card:', error);
      alert('Failed to download card. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  const handleContactSupport = () => {
    setShowSupportModal(true);
  };

  const closeSupportModal = () => {
    setShowSupportModal(false);
  };

  return (
    <div className="single-page-app">
      {/* Logo Container - shown on all steps */}
      <div className="logo-container">
        <img src={`${process.env.PUBLIC_URL}/mySimpleRXLogo.png`} alt="MySimpleRX Logo" className="logo" />
        {officeCode && (
          <img src={`/api/logo/${encodeURIComponent(officeCode)}`} alt="Office Logo" className="office-logo" />
        )}
      </div>

      {/* Main Content Container */}
      <div className="main-content">

        {/* Contact Info Step */}
        {currentStep === 'contact-info' && (
          <div className="step-container">
            <div className="step-card">
              <div className="step-header">
                <h2>Patient Enrollment</h2>
                <p className="subtitle">Please provide your information to complete your enrollment.</p>
              </div>
              
              <form onSubmit={handleContactSubmit} className="step-form">
                <div className="form-section">
                  <h3>Personal Information</h3>
                  <div className="form-row">
                    <div className="form-group contact-info">
                      <label htmlFor="firstName">First Name</label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={contactFormData.firstName}
                        onChange={handleContactInputChange}
                        required
                        className={`form-control ${contactErrors.firstName ? 'error' : ''}`}
                        placeholder="Enter your first name"
                        maxLength={50}
                        autoComplete="given-name"
                      />
                        {contactErrors.firstName && <span className="error-message">{contactErrors.firstName}</span>}
                    </div>

                    <div className="form-group contact-info">
                      <label htmlFor="lastName">Last Name</label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={contactFormData.lastName}
                        onChange={handleContactInputChange}
                        required
                        className={`form-control ${contactErrors.lastName ? 'error' : ''}`}
                        placeholder="Enter your last name"
                        maxLength={50}
                        autoComplete="family-name"
                      />
                        {contactErrors.lastName && <span className="error-message">{contactErrors.lastName}</span>}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group contact-info">
                      <label htmlFor="email">Email Address</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={contactFormData.email}
                        onChange={handleContactInputChange}
                        placeholder="example@gmail.com"
                        className={`form-control ${contactErrors.email ? 'error' : ''}`}
                        maxLength={254}
                        autoComplete="email"
                      />
                    </div>

                    <div className="form-group contact-info">
                      <label htmlFor="phone">Phone Number</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={contactFormData.phone}
                        onChange={handleContactInputChange}
                        placeholder="(555) 123-4567"
                        className={`form-control ${contactErrors.phone ? 'error' : ''}`}
                        maxLength={14}
                        autoComplete="tel"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className={`form-group contact-info ${!showOfficeCodeDropdown ? 'date-of-birth-full-width' : ''}`}>
                      <label htmlFor="dateOfBirth">Date of Birth</label>
                      <input
                        type="date"
                        id="dateOfBirth"
                        name="dateOfBirth"
                            value={contactFormData.dateOfBirth}
                            onChange={handleContactInputChange}
                        required
                            className={`form-control ${contactErrors.dateOfBirth ? 'error' : ''}`}
                        autoComplete="bday"
                        max={new Date().toISOString().split('T')[0]}
                        min="1900-01-01"
                      />
                          {contactErrors.dateOfBirth && <span className="error-message">{contactErrors.dateOfBirth}</span>}
                    </div>

                    {showOfficeCodeDropdown && (
                      <div className="form-group contact-info">
                        <label htmlFor="officeCode">Office Location</label>
                        <select
                          id="officeCode"
                          name="officeCode"
                          value={contactFormData.officeCode}
                          onChange={(e) => {
                            setOfficeCode(e.target.value);
                            handleContactInputChange(e);
                          }}
                          required
                          className={`form-control ${contactErrors.officeCode ? 'error' : ''}`}
                          disabled={isLoadingOfficeCodes}
                        >
                          {isLoadingOfficeCodes ? (
                            <option value="">Loading locations...</option>
                          ) : (
                            <>
                              <option value="">Select a location</option>
                              {availableOfficeCodes.map((location) => (
                                <option key={location.name} value={location.name}>
                                  {location.name}
                                </option>
                              ))}
                            </>
                          )}
                        </select>
                        {contactErrors.officeCode && <span className="error-message">{contactErrors.officeCode}</span>}
                      </div>
                    )}
                  </div>
                </div>

                

                <div className="form-section address-info">
                  <div className="address-section-header"
                  onClick={() => setShowAddressSection(!showAddressSection)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setShowAddressSection(!showAddressSection);
                    }
                  }}>
                    <h3>Address Information (Optional)</h3>
                    <div className="address-toggle-indicator">
                      <span className="material-icons">
                        {showAddressSection ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                      </span>
                    </div>
                  </div>
                  
                  {showAddressSection && (
                    <div className="address-fields">
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="address">Street Address</label>
                          <input
                            type="text"
                            id="address"
                            name="address"
                            value={contactFormData.address}
                            onChange={handleContactInputChange}
                            className={`form-control ${contactErrors.address ? 'error' : ''}`}
                            maxLength={100}
                            autoComplete="street-address"
                          />
                            {contactErrors.address && <span className="error-message">{contactErrors.address}</span>}
                          </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group contact-info">
                          <label htmlFor="city">City</label>
                          <input
                            type="text"
                            id="city"
                            name="city"
                            value={contactFormData.city}
                            onChange={handleContactInputChange}
                            className={`form-control ${contactErrors.city ? 'error' : ''}`}
                            maxLength={50}
                            autoComplete="address-level2"
                          />
                          {contactErrors.city && <span className="error-message">{contactErrors.city}</span>}
                        </div>

                        <div className="form-group contact-info">
                          <label htmlFor="state">State</label>
                          <input
                            type="text"
                            id="state"
                            name="state"
                            value={contactFormData.state}
                            onChange={handleContactInputChange}
                            maxLength="2"
                            placeholder="CA"
                            className={`form-control ${contactErrors.state ? 'error' : ''}`}
                            autoComplete="address-level1"
                          />
                          {contactErrors.state && <span className="error-message">{contactErrors.state}</span>}
                        </div>

                        <div className="form-group contact-info">
                          <label htmlFor="zipCode">ZIP Code</label>
                          <input
                            type="text"
                            id="zipCode"
                            name="zipCode"
                            value={contactFormData.zipCode}
                            onChange={handleContactInputChange}
                            placeholder="12345"
                            className={`form-control ${contactErrors.zipCode ? 'error' : ''}`}
                            maxLength={10}
                            autoComplete="postal-code"
                          />
                          {contactErrors.zipCode && <span className="error-message">{contactErrors.zipCode}</span>}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {error && <div className="error-message">{error}</div>}

                {/* Legal Documents Checkbox */}
                {documents.length > 0 && (
                  <div className="legal-documents-checkbox" onClick={handleLegalDocumentsModalOpen}>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={allAccepted}
                        readOnly
                        className={contactErrors.legalDocuments ? 'error' : ''}
                      />
                      <span className="checkbox-text">
                        I have read and agree to the{' '}
                        <button
                          type="button"
                          
                          className="legal-documents-link"
                        >
                          terms and conditions
                        </button>
                        {/* {' '}required for enrollment
                        {!allAccepted && (
                          <span className="checkbox-hint">
                            {' '}(Please read and accept all documents in the modal)
                          </span>
                        )}
                        {allAccepted && (
                          <span className="checkbox-success">
                            {' '}✓ All documents accepted
                          </span>
                        )} */}
                      </span>
                    </label>
                    {contactErrors.legalDocuments && (
                      <div className="error-message">{contactErrors.legalDocuments}</div>
                    )}
                  </div>
                )}

                {/* Marketing Consent Checkbox */}
                <div className="marketing-consent-checkbox">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={marketingConsent}
                      onChange={(e) => {
                        setMarketingConsent(e.target.checked);
                        if (contactErrors.marketingConsent) {
                          setContactErrors(prev => ({
                            ...prev,
                            marketingConsent: ''
                          }));
                        }
                      }}
                      className={contactErrors.marketingConsent ? 'error' : ''}
                    />
                    <span className="checkbox-text">
                      I agree to receive text messages and emails from MySimpleRX
                    </span>
                  </label>
                  {contactErrors.marketingConsent && (
                    <div className="error-message">{contactErrors.marketingConsent}</div>
                  )}
                </div>

                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="loading-icon"></span>
                        Processing Enrollment...
                      </>
                    ) : (
                      'Complete Enrollment'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Thank You Step */}
        {currentStep === 'thank-you' && (
          <div className="step-container">
            <div className="step-card">
              <div className="success-header">
                {isUpdate ? (
                  <>
                    <h2>Information Updated Successfully!</h2>
                    <p className="subtitle">Your contact information has been updated in our system.</p>
                    <p className="subtitle">Your existing coverage remains active and unchanged.</p>
                    <h3 className="welcome-header">Thank you for keeping your information current!</h3>
                  </>
                ) : (
                  <>
                    <h2>Enrollment Complete!</h2>
                    <p className="subtitle">Thank you for choosing MySimpleRX</p>
                    <p className="subtitle">Your enrollment has been completed successfully.</p>
                    <p className="subtitle">You can start using your coverage immediately. Present your card at any participating pharmacy.</p>
                    <h3 className="welcome-header">Welcome to MySimpleRX!</h3>
                  </>
                )}
              </div>
              
              <div className="download-card-section">
                <div className="download-card-content">
                  <div className="download-card-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M16 13H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M16 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="download-card-text">
                    <h4>Download Your Discount Card</h4>
                    <p>Get your discount card to use at participating pharmacies</p>
                  </div>
                  <button 
                    onClick={handleDownloadCard} 
                    className="btn btn-download"
                    disabled={loading}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Download Card
                  </button>
                </div>
              </div>

              <div className="member-offers">
                {isLoadingOffers ? (
                  <div className="offers-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading offers...</p>
                  </div>
                ) : offersError ? (
                  <div className="offers-error">
                    <p>{offersError}</p>
                    <button 
                      onClick={fetchOffers} 
                      className="btn btn-outline"
                    >
                      Try Again
                    </button>
                  </div>
                ) : offers.length > 0 ? (
                  <div className="offers-grid">
                    {offers.map((offer) => (
                      <div key={offer.id} className="offer-card">
                        <h4>{offer.title}</h4>
                        <p>{offer.description}</p>
                        <button className="btn btn-outline" onClick={() => window.open(offer.link, '_blank')}>Learn More</button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-offers">
                    <p>No offers available at this time.</p>
                  </div>
                )}
              </div>

              <div className="action-buttons">
                {isUpdate ? (
                  <>
                    <button onClick={handleContactSupport} className="btn btn-secondary">
                      Contact Support
                    </button>
                    <button onClick={handleStartOver} className="btn btn-primary">
                      Start New Enrollment
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={handleContactSupport} className="btn btn-secondary">
                      Contact Support
                    </button>
                    <button onClick={handleStartOver} className="btn btn-primary">
                      Start New Enrollment
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Enrollment Confirmation Popup Modal */}
      {showEnrollmentPopup && (
        <div className="enrollment-popup-overlay" onClick={handleEnrollmentCancel}>
          <div className="enrollment-popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="enrollment-popup-header">
              <h3>Enrollment Status</h3>
            </div>
            <div className="enrollment-popup-body">
                <div className="success-message">
                <h3>You are already enrolled!</h3>
                {enrollmentData?.isEnrolledAtCurrent && (
                    <>
                      <p>You are already enrolled at this location.</p>
                    <p>Would you like to update your information?</p>
                    </>
                  )}
                {enrollmentData?.isEnrolledElsewhere && (
                  <>
                    <p>You are already enrolled at another location.</p>
                  <p>Would you like to enroll at this location and update your information?</p>
                  </>
                )}

                {/* {documents.length > 0 && (
                  <p className="info-text">
                  You will need to review and acknowledge {documents.length} legal document{documents.length !== 1 ? 's' : ''} before continuing.
                  </p>
                )} */}
                  
                <div className="button-group">
                  <button onClick={handleEnrollmentCancel} className="btn btn-secondary">
                    Cancel
                  </button>
                  <button onClick={handleEnrollmentContinue} className="btn btn-primary">
                    Continue
                  </button>
                </div>
                </div>
            </div>
          </div>
        </div>
      )}

      {/* Legal Documents Modal */}
      {showLegalDocumentsModal && (
        <div className="legal-documents-modal-overlay" onClick={handleLegalDocumentsModalClose}>
          <div className="legal-documents-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="legal-documents-modal-header">
              <h3>Terms and Conditions</h3>
              <button className="legal-documents-modal-close" onClick={handleLegalDocumentsModalClose}>
                ×
              </button>
            </div>
            <div className="legal-documents-modal-body">
              <div className="legal-documents">
                {documents.map((document, index) => (
                  <div key={document.id} className="legal-document">
                    <div className="document-header">
                      <div className="document-header-left">
                        <h4>{document.title}</h4>
                      </div>
                      <button
                        type="button"
                        className="document-toggle"
                        onClick={() => handleToggleDocument(document.id)}
                        aria-expanded={expandedDocuments[document.id] || false}
                        aria-controls={`doc-content-${document.id}`}
                      >
                        <span className="toggle-text">
                          {expandedDocuments[document.id] ? 'Collapse' : 'Read Document'}
                        </span>
                        <span className="toggle-icon">
                          {expandedDocuments[document.id] ? '−' : '+'}
                        </span>
                      </button>
                    </div>
                    
                    {expandedDocuments[document.id] && (
                      <div className="document-content">
                        <div
                          id={`doc-content-${document.id}`}
                          ref={(el) => { contentRefs.current[document.id] = el; }}
                          className="document-content-scroll"
                          onScroll={() => handleScroll(document.id)}
                        >
                          <div dangerouslySetInnerHTML={{ __html: document.content }} />
                        </div>
                        <div className="read-hint" aria-live="polite">
                          {readStatus[document.id] ? 'You have reached the end. You may accept this document.' : 'Scroll to the bottom to accept.'}
                        </div>
                      </div>
                    )}
                    
                    <label className={`checkbox-label ${!readStatus[document.id] ? 'disabled' : ''}`}>
                      <input
                        type="checkbox"
                        checked={acknowledgments[document.id] || false}
                        onChange={(e) => handleAcknowledgmentChange(document.id, e.target.checked)}
                        disabled={!readStatus[document.id]}
                        title={!readStatus[document.id] ? 'Read the document to enable' : ''}
                      />
                      <span className="checkbox-text">
                        I have read and agree to the {document.title}
                      </span>
                    </label>
                  </div>
                ))}
              </div>

              <div className="legal-summary">
                <div className="summary-header">
                  <h4>Document Acceptance Summary</h4>
                  <div className={`acceptance-status ${allAccepted ? 'accepted' : 'pending'}`}>
                    {allAccepted ? '✓ All Documents Accepted' : 'Documents Pending'}
                  </div>
                </div>
                <div className="summary-items">
                  {documents.map((document) => (
                    <div key={document.id} className={`summary-item ${acknowledgments[document.id] ? 'accepted' : 'pending'}`}>
                      <span className={`item-icon ${acknowledgments[document.id] ? 'accepted' : 'pending'}`}>
                        {acknowledgments[document.id] ? '✓' : '○'}
                      </span>
                      <span>{document.title}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="legal-documents-modal-footer">
                <button onClick={handleLegalDocumentsModalClose} className="btn btn-secondary">
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Support Modal */}
      {showSupportModal && (
        <div className="support-modal-overlay" onClick={closeSupportModal}>
          <div className="support-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="support-modal-header">
              <h3>Customer Support</h3>
              <button className="support-modal-close" onClick={closeSupportModal}>
                ×
              </button>
            </div>
            <div className="support-modal-body">
              <div className="support-info">
                <p><strong>Email:</strong> support@mysimplerx.com (example email)</p>
                <p><strong>Phone:</strong> 1-800-746-7537 (example phone number)</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SinglePageApp;
